import React, { useMemo, useState } from 'react';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useRevenueView } from '../context/RevenueViewContext';
import { fmtMoney } from '../lib/format';
import { amountForView, amountForTalent, REVENUE_VIEWS, dealTalentIds } from '../lib/deals';
import { filterByRange } from '../lib/dateRange';
import DateRangeFilter from '../components/DateRangeFilter';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { TrendingUp, Users, Building2, CalendarDays, CalendarRange, CalendarClock, PoundSterling } from 'lucide-react';
import InitialsAvatar from '../components/InitialsAvatar';

const MONEY_STAGES = ['rate_confirmed', 'contracted', 'complete'];

const StatCard = ({ title, value, sub, Icon, tint }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="flex items-center justify-between">
      <div className="text-sm text-slate-500">{title}</div>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></div>
    </div>
    <div className="mt-3 text-2xl font-semibold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 mt-1">{sub}</div>
  </div>
);

const Bar = ({ pct, color = '#d394ab' }) => (
  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
  </div>
);

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const Reports = () => {
  const { deals, talent, brands, talentMap, brandMap } = useDeals();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const { view, setView } = useRevenueView();
  const [dateRange, setDateRange] = useState('this_year');
  const [talentFilter, setTalentFilter] = useState('all');

  const filtered = useMemo(() => {
    const inRange = filterByRange(deals, dateRange, 'created_at');
    if (talentFilter === 'all') return inRange;
    return inRange.filter((d) => dealTalentIds(d).includes(talentFilter));
  }, [deals, dateRange, talentFilter]);

  // ----- Top-row snapshot stats (moved from the old Dashboard) --------------
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const quarterStartMonth = quarter * 3;
  const snapshotStats = useMemo(() => {
    const bookedDeals = deals.filter((d) => MONEY_STAGES.includes(d.stage));
    const inMonth = (d) => {
      if (!d.created_at) return false;
      const dt = new Date(d.created_at);
      return dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth();
    };
    const inQuarter = (d) => {
      if (!d.created_at) return false;
      const dt = new Date(d.created_at);
      return dt.getFullYear() === now.getFullYear() && dt.getMonth() >= quarterStartMonth && dt.getMonth() < quarterStartMonth + 3;
    };
    const inYear = (d) => {
      if (!d.created_at) return false;
      return new Date(d.created_at).getFullYear() === now.getFullYear();
    };
    const sum = (arr) => arr.reduce((s, d) => {
      const primaryId = dealTalentIds(d)[0];
      const primary = primaryId ? talentMap[primaryId] : null;
      return s + amountForView(d, view, primary);
    }, 0);
    const monthTotal = sum(bookedDeals.filter(inMonth));
    const quarterTotal = sum(bookedDeals.filter(inQuarter));
    const yearArr = bookedDeals.filter(inYear);
    const yearTotal = sum(yearArr);
    const avgDealValue = yearArr.length ? yearTotal / yearArr.length : 0;
    return { monthTotal, quarterTotal, yearTotal, avgDealValue, yearCount: yearArr.length };
  }, [deals, talentMap, view, now, quarterStartMonth]);
  const monthLabel = now.toLocaleString('en-GB', { month: 'long' });
  const qLabel = `Q${quarter + 1} ${now.getFullYear()}`;
  const yearLabel = `${now.getFullYear()} so far`;
  const viewSub = { full: '100% value', talent: "talent's share", agency: 'agency cut' }[view];
  const avgLabel = `avg deal (${REVENUE_VIEWS.find((v) => v.id === view)?.label})`;
  const avgSub = { full: 'average full deal value', talent: 'average talent take-home per booked deal this year', agency: 'average LVE Social commission per booked deal this year' }[view];

  const byTalent = useMemo(() => {
    const acc = {};
    filtered.forEach((d) => {
      const ids = dealTalentIds(d);
      if (ids.length === 0) {
        const key = '__none__';
        if (!acc[key]) acc[key] = { name: 'unassigned', total: 0, picture: null };
        acc[key].total += amountForView(d, view, null);
        return;
      }
      const primary = talentMap[ids[0]];
      ids.forEach((id) => {
        const t = talentMap[id];
        if (!t) return;
        const key = t.user_id;
        if (!acc[key]) acc[key] = { name: t.name, total: 0, picture: t.picture };
        acc[key].total += ids.length === 1
          ? amountForView(d, view, primary)
          : amountForTalent(d, view, id, primary);
      });
    });
    return Object.values(acc).sort((a, b) => b.total - a.total);
  }, [filtered, talentMap, view]);

  const byBrand = useMemo(() => {
    const acc = {};
    filtered.forEach((d) => {
      const b = d.brand_id ? brandMap[d.brand_id] : null;
      const primaryId = dealTalentIds(d)[0];
      const primary = primaryId ? talentMap[primaryId] : null;
      const key = b?.id || '__none__';
      const name = b?.name || 'unbranded';
      if (!acc[key]) acc[key] = { name, logo: b?.logo, total: 0 };
      acc[key].total += amountForView(d, view, primary);
    });
    return Object.values(acc).sort((a, b) => b.total - a.total);
  }, [filtered, brandMap, talentMap, view]);

  const byMonth = useMemo(() => {
    const now = new Date();
    const rows = MONTHS.map((label, i) => ({ label, month: i, year: now.getFullYear(), total: 0 }));
    filtered.forEach((d) => {
      if (!d.created_at) return;
      const dt = new Date(d.created_at);
      if (dt.getFullYear() !== now.getFullYear()) return;
      const primaryId = dealTalentIds(d)[0];
      const primary = primaryId ? talentMap[primaryId] : null;
      rows[dt.getMonth()].total += amountForView(d, view, primary);
    });
    return rows;
  }, [filtered, talentMap, view]);

  const grandTotal = filtered.reduce((s, d) => {
    const primaryId = dealTalentIds(d)[0];
    const primary = primaryId ? talentMap[primaryId] : null;
    return s + amountForView(d, view, primary);
  }, 0);
  const maxT = Math.max(1, ...byTalent.map((r) => r.total));
  const maxB = Math.max(1, ...byBrand.map((r) => r.total));
  const maxM = Math.max(1, ...byMonth.map((r) => r.total));

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">reports</h1>
          <p className="text-sm text-slate-500 mt-1">revenue by talent, brand and month. respects the 100% / talent / agency view.</p>
        </div>
      </header>

      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden text-sm" data-testid="reports-snapshot-view-toggle">
          {REVENUE_VIEWS.map((v) => (
            <button key={v.id} onClick={() => setView(v.id)} title={v.hint} data-testid={`reports-snapshot-view-${v.id}`} className={`px-3 h-9 ${view === v.id ? 'bg-mint-50 text-mint-700 font-medium' : 'text-slate-600 hover:bg-slate-50'} ${v.id !== 'full' ? 'border-l border-slate-200' : ''}`}>{v.label}</button>
          ))}
        </div>
        <span className="text-xs text-slate-400 hidden md:inline">showing <span className="font-medium text-slate-600">{REVENUE_VIEWS.find((v) => v.id === view)?.hint}</span></span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard title={monthLabel} value={fmtMoney(snapshotStats.monthTotal)} sub={`this month · ${viewSub}`} Icon={CalendarDays} tint="bg-pink-100 text-pink-700" />
        <StatCard title={qLabel} value={fmtMoney(snapshotStats.quarterTotal)} sub={`this quarter · ${viewSub}`} Icon={CalendarRange} tint="bg-lilac-100 text-lilac-700" />
        <StatCard title={yearLabel} value={fmtMoney(snapshotStats.yearTotal)} sub={`ytd · ${viewSub}`} Icon={CalendarClock} tint="bg-slate-100 text-slate-700" />
        <StatCard title={avgLabel} value={fmtMoney(snapshotStats.avgDealValue)} sub={`${avgSub} · ${snapshotStats.yearCount} deal${snapshotStats.yearCount === 1 ? '' : 's'}`} Icon={PoundSterling} tint="bg-emerald-50 text-emerald-600" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
        {isManager && (
          <Select value={talentFilter} onValueChange={setTalentFilter}>
            <SelectTrigger className="h-9 w-44 bg-white text-sm" data-testid="reports-talent-select"><SelectValue placeholder="all talent" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all talent</SelectItem>
              {talent.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">total in range</div>
          <div className="text-xl font-semibold text-slate-900">{fmtMoney(grandTotal)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-slate-400" /> by talent</h3>
          {byTalent.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">no data</p> : (
            <div className="space-y-3">
              {byTalent.map((r) => (
                <div key={r.name}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <div className="flex items-center gap-2">
                      {r.name && <InitialsAvatar name={r.name} size={20} />}
                      <span className="text-slate-600">{r.name}</span>
                    </div>
                    <span className="font-medium text-slate-700">{fmtMoney(r.total)}</span>
                  </div>
                  <Bar pct={(r.total / maxT) * 100} color="#d394ab" />
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4"><Building2 className="w-4 h-4 text-slate-400" /> by brand</h3>
          {byBrand.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">no data</p> : (
            <div className="space-y-3">
              {byBrand.map((r) => (
                <div key={r.name}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-600">{r.logo ? `${r.logo} ` : ''}{r.name}</span>
                    <span className="font-medium text-slate-700">{fmtMoney(r.total)}</span>
                  </div>
                  <Bar pct={(r.total / maxB) * 100} color="#a889bd" />
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-slate-400" /> by month ({new Date().getFullYear()}){talentFilter !== 'all' && talentMap[talentFilter] ? ` · ${talentMap[talentFilter].name}` : ''}</h3>
          <div className="grid grid-cols-6 md:grid-cols-12 gap-3 items-end" style={{ minHeight: 160 }}>
            {byMonth.map((r) => (
              <div key={r.label} className="flex flex-col items-center gap-1">
                <div className="text-[10px] text-slate-500">{r.total > 0 ? fmtMoney(r.total) : ''}</div>
                <div className="w-full bg-slate-100 rounded-t-md relative" style={{ height: `${Math.max(4, (r.total / maxM) * 140)}px` }}>
                  <div className="absolute inset-x-0 bottom-0 rounded-t-md" style={{ height: '100%', background: '#d394ab' }} />
                </div>
                <div className="text-[10px] text-slate-500 mt-1">{r.label}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Reports;
